import React, { useState, useEffect } from 'react';
import { useUseCases } from '../../hooks/useUseCases';
import { useTheme } from '../../hooks/useTheme';
import { useParams } from 'react-router-dom';
import MainLayout from "core/components/layout/MainLayout";
import Loading from "core/components/Loading";
import Error from "core/components/Error";
import BreadcrumbWidget from "core/components/shared/BreadcrumbWidget";
import TimeAgoWidget from "core/components/shared/TimeAgoWidget";
import UrlSourceWidget from '../../components/UrlSourceWidget';
import UseCasesSearch from '../../components/UseCasesSearch';
import TruncatedDescriptionWidget from '../../components/TruncatedDescriptionWidget';
import Pagination from 'core/components/shared/Pagination';
import ExportButtons from 'core/components/shared/ExportButtons';
import PusherListener from 'features/feature_chat/components/PusherListener';
import UseCaseDetails from '../../components/UseCaseDetails';
import RelevanceScoreWidget from '../../components/RelevanceScoreWidget';
import FeatureThemeWidget from '../../components/FeatureThemeWidget';
import {ContentHttpService} from '../../services/index';
import Utils from 'core/utils';

const UseCasesPage: React.FC = () => {
  const { themeId, themeSlug } = useParams<{ themeId: string; themeSlug?: string }>();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [thoughts, setThoughts] = useState<string[]>([]);
  const [currentThought, setCurrentThought] = useState<string>('');
  const [selectedUseCase, setSelectedUseCase] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [progress, setProgress] = useState<number>(0);
  const { theme, loading: themeLoading } = useTheme(themeId ? parseInt(themeId) : undefined);
  const { useCases, totalCount, isLoading, handleGenerateReport, refetch } = useUseCases({ 
    page, 
    pageSize,
    searchQuery,
    themeId: themeId ? parseInt(themeId) : undefined
  });



  useEffect(() => {
    const checkReportStatus = async () => {
      if (themeId) {
        try {
          const report:any = await ContentHttpService.findReportByTheme(parseInt(themeId));
          
          setReport(report);
          setIsGenerating(report.metadata?.status === 'RUNNING');
        } catch (error) {
          console.error('Error checking report status:', error);
          setIsGenerating(false);
        }
      }
    };

    checkReportStatus();
  }, [themeId]);

  const handleThoughtReceived = (thought: string) => {
    console.log(thought);
    setThoughts([...thoughts, thought]);
    setCurrentThought(thought);
  };
  
  const handleStopGeneration = async () => {
    try {
      await ContentHttpService.stopReportGeneration(themeId ? parseInt(themeId) : undefined);
      setIsGenerating(false);
    } catch (error) {
      console.error('Error stopping report generation:', error);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Handle search from the search widget
  const handleSearch = (queryString: string) => {
    setSearchQuery(queryString);
    setPage(1); // Reset to first page when searching
  };
  
  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleUseCaseClick = (useCase: any) => {
    setSelectedUseCase(useCase);
    setShowDetails(true);
  };

  const handleUseCaseReceived = (useCase: any) => {
    refetch();
  };

  const handleProgress = (progress: number) => {
    console.log(progress);
    setProgress(progress);
  };

  return (
    <MainLayout>
      <div className="container-fluid">
        <BreadcrumbWidget
          mainTitle={`Use Cases ${themeId ? `- ${theme?.title.replace('-', ' ') || themeSlug || `Theme ${themeId}`}` : ''}`}
          subTitle={totalCount ? `(${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount})` : ''}
          breadcrumbs={[
            { title: "Dashboard", url: "/" },
            { title: "Use Case Library", url: "/usecases" },
            { title: themeId ? theme?.title.replace('-', ' ') || themeSlug || `Theme ${themeId}` : "All Use Cases" },
          ]}
        />

        <div className="row">
          <div className="col-xl-12">
            <div className="card custom-card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                <UseCasesSearch 
                    onSearch={handleSearch}
                    themeId={themeId ? parseInt(themeId) : undefined}
                  />
                </h5>
                <div className="d-flex align-items-center gap-2">
                  
                  <ExportButtons endpointUrl="/content/use-cases/" searchQuery={searchQuery} themeId={themeId ? parseInt(themeId) : undefined} />

                    <div className="last-update">
                      {themeId && (
                        <div className="d-flex align-items-center gap-2">
                              <>
                                {isGenerating ? (
                                  <button
                                    className="btn btn-outline-danger p-1 me-2"
                                    onClick={handleStopGeneration}
                                  >
                                    <i className="ri-stop-circle-line align-middle me-1"></i>
                                    Stop                                 </button>
                                ) : (
                                  <button
                                    className="btn btn-outline-success p-1 me-2"
                                    onClick={() => { setIsGenerating(true); handleGenerateReport(themeId ? parseInt(themeId) : undefined);  }}
                                  >
                                    <i className="ri-play-circle-line align-middle me-1"></i>
                                    Refresh
                                  </button>
                                )}
                              </>
                            <small style={{ fontSize: "10px" }}>Last updated: <TimeAgoWidget date={report?.updated_at} /></small>
                          </div>
                        )}
                    </div>
                    {themeId && <FeatureThemeWidget themeId={parseInt(themeId)} />}

                </div>
              </div>
              
              <div className="card-body">
                {/* Progress bar for report generation */}
                {/* {progress && (
                  <div className="mb-3">
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                        role="progressbar"
                        style={{ width: `${progress}%` }}
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    </div>
                    <div className="text-end small text-muted mt-1">{progress}%</div>
                  </div>
                )} */}
                
                {isLoading && <Loading isLoading={isLoading} />}
                
                {(!isLoading && (!useCases || useCases.length === 0) && !currentThought && !isGenerating) && (
                  <div className="alert alert-info" role="alert">
                    No use cases found. {themeId && "Start a new search to get started."}
                  </div>
                )}
                
                {useCases && useCases.length > 0 && (
                  <>
                    {/* {searchQuery && (
                      <div className="alert alert-info mb-3">
                        Showing results for: <strong>{decodeURIComponent(searchQuery.replace(/&/g, ', ').replace(/=/g, ': '))}</strong>
                        <button 
                          className="btn btn-sm btn-link float-end" 
                          onClick={() => {
                            setSearchQuery('');
                            setPage(1);
                          }}
                        >
                          Clear search
                        </button>
                      </div>
                    )} */}

                    {currentThought && (
                      <div className="alert alert-info" role="alert">
                        {currentThought}
                      </div>
                    )}
                    
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>ID</th>
                            <th>Name</th> 
                            <th>Use Case Type</th>
                            <th>Company</th>
                            <th>Industry</th>
                            <th>Tools</th>
                            <th>Description</th>
                            <th>Performance Impact</th>
                            <th>Date</th>
                            <th>Source</th>
                            <th title="Relevancy score. its is based on how relevant is the use case to the prompt. 10 is the highest and 1 is the lowest.">Score</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {useCases.map((useCase) => (
                            <tr key={useCase.id}>
                              <td>{useCase.id}</td>
                              <td className="col-md-2">
                                <a 
                                  href={`/usecases/${useCase.id}`}
                                  className="p-0 text-decoration-none " 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleUseCaseClick(useCase);
                                  }}
                                >
                                  {useCase.use_case_name}
                                </a>
                              </td>
                              <td className="">{useCase.use_case_type}</td>
                              <td className="">{useCase.company}</td>
                              <td className="">{useCase.industry || 'N/A'}</td>
                              <td className="col-md-2">
                                {useCase.tools?.split(',').map((tool: string, index: number) => (
                                  <span key={index} className="badge bg-primary me-1 mb-1" style={{ maxWidth: '150px', fontWeight: 'normal', fontSize: '12px', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                    {tool.trim()}
                                  </span>
                                ))}
                              </td>
                              <td className="col-md-4">
                                <TruncatedDescriptionWidget description={useCase.use_case_description} maxLength={100} />
                              </td>
                              <td className="col-md-2">
                                <TruncatedDescriptionWidget description={useCase.performance_impact} maxLength={100} />
                              </td>
                              <td>{formatDate(useCase.use_case_date)}</td>
                              <td>
                                {useCase.source ? (
                                  <UrlSourceWidget showScore={false} source={useCase.source} urlValidationScore={useCase.url_validation_score} />
                                ) : (
                                  ''
                                )}
                              </td>
                              <td>
                                <RelevanceScoreWidget score={useCase.relevance_score} />
                              </td>
                              <td>
                                <TimeAgoWidget date={useCase.created_at} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <Pagination
                        currentPage={page}
                        totalItems={totalCount}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                      />
                      {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount}
                    </div>
                    
                    
                  </>
                )}

                {themeId && (
                  <PusherListener streamKey={"use_cases_"+themeId} onThoughtReceived={handleThoughtReceived} onUseCaseReceived={handleUseCaseReceived} onProgress={handleProgress} />
                )}
                {currentThought && (
                  <div className="alert alert-info mt-3" role="alert">
                    {currentThought}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UseCaseDetails
        useCase={selectedUseCase}
        show={showDetails}
        onHide={() => setShowDetails(false)}
      />
    </MainLayout>
  );
};

export default UseCasesPage; 